import React from 'react';
import styles from './index.module.less';
import type { IMultiSiteStoreFetchRes } from '@/reolink/system-pages/multi-site-page/fetch';
import ReoI18n, { useTranslation } from '@/modules/ReoI18n';
import i18n from 'i18next';
import { ThemeConfigContext, type IScene, type ITheme, type IThemeConfig } from '@reolink-web/components.basic';
import { FooterContext, getIsEuCountry } from './common';
import CompFooterLinkGroups from './components/FooterLinkGroups';
import CompFooterBrandInfo from './components/FooterBrandInfo';
import CompFooterCopyright from './components/FooterCopyright';
import eventBus from '@/utils/event';
import CompFooterOtherInfo from './components/FooterOtherInfo';
import * as utils from '@reolink-web/utils';
import CompAcceptPayWithSocial from './components/AcceptPayWithSocial';
import { getCDNPath } from '@/utils/function/getCDNPath';
import loadStyle from '@/utils/browser/loadStyle';
import loadScript from '@/utils/browser/loadScript';
import type { ISupportCountryItem } from '@/common';
const CompReolinkFooter: React.FC<{ ctx: IMultiSiteStoreFetchRes; }> = (props) => {
    const reoI18n = React.useMemo(() => {
        const reoI18n = new ReoI18n();
        reoI18n.init({
            i18nInstance: i18n,
            currentLang: 'en',
        });
        return reoI18n;
    }, []);
    reoI18n.loadResource(
        ['relink-footer'],
        {
            ...props.ctx.i18nPackage,
        },
        'en',
    );
    const { t } = useTranslation();

    const [countryTitle, setCountryTitle] = React.useState<string>('');
    const [countryList, setCountryList] = React.useState<ISupportCountryItem[]>([]);

    const [isDistributor, setIsDistributor] = React.useState<boolean>(false);

    const isEuCountry = React.useMemo(() => {
        return getIsEuCountry(countryList, props.ctx.country);
    }, [countryList, props.ctx.country]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const otherInfoRef = React.useRef<HTMLDivElement>(null);

    const getTextByKey = React.useCallback(
        (key: string) => {
            const text = t(`reolink-footer:${key}`);
            return text ? text : key;
        },
        [t],
    );

    const footerContextValue = React.useMemo(() => {
        return {
            lang: props.ctx.lang,
            country: props.ctx.country,
            getTextByKey,
            countryTitle,
            countryList,
            isEuCountry,
            isDistributor,
        };
    }, [countryList, countryTitle, getTextByKey, isDistributor, isEuCountry, props.ctx.country, props.ctx.lang]);

    React.useEffect(() => {
        const handleCountryChange = (data: {
            countryTitle: string;
            countryList: ISupportCountryItem[];
            isDistributor: boolean;
        }): void => {
            setCountryTitle(data.countryTitle);
            setCountryList(data.countryList);
            setIsDistributor(data.isDistributor);
        };
        eventBus.on('countryLangChange', handleCountryChange);

        return () => {
            eventBus.off('countryLangChange', handleCountryChange);
        };
    }, []);

    const [scene, setScene] = React.useState<IScene>('reolink');
    const [theme, setTheme] = React.useState<ITheme>('light');
    const setSceneCallback = React.useCallback((scene: IScene): void => {
        setScene(scene);
    }, []);
    const setThemeCallback = React.useCallback((theme: ITheme): void => {
        setTheme(theme);
    }, []);
    const themeConfig = React.useMemo((): IThemeConfig => {
        return {
            scene,
            theme,
            setScene: setSceneCallback,
            setTheme: setThemeCallback,
        };
    }, [scene, theme, setSceneCallback, setThemeCallback]);

    React.useEffect(() => {
        const handleScroll = (): void => {
            if (containerRef.current && otherInfoRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const isAtBottom = rect.bottom <= window.innerHeight + 10;
                if (isAtBottom) {
                    otherInfoRef.current.style.display = 'block';
                    containerRef.current.style.marginBottom = `264px`;
                    document.body.style.setProperty('max-height', 'unset');
                }
                else {
                    otherInfoRef.current.style.display = 'none';
                    containerRef.current.style.marginBottom = '0px';
                    document.body.style.removeProperty('max-height');
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    React.useEffect(() => {
        const container = document.createElement('div');
        container.id = 'reolinkSiteNotice';
        document.body.appendChild(container);
        (async () => {
            await Promise.all([
                loadStyle(getCDNPath(`/rafa/render/css/main.css?v=${__BUILD_TIME__}`), document.querySelector('head')!),
                loadScript(getCDNPath(`/rafa/render/js/index.js?v=${__BUILD_TIME__}`), document.body),
            ]);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (window as any).RafaUt.render('tipsPage', 'reolinkSiteNotice', props.ctx.lang!, [
                'common',
                'zendesk',
            ]);
        })().catch(console.error);
        return () => {
            container.remove(); // 确保组件卸载时清理DOM
        };
    }, [props.ctx.lang]);

    return (
        <ThemeConfigContext.Provider value={ themeConfig }>
            <FooterContext.Provider value={ footerContextValue }>
                <div
                    ref={ containerRef }
                    className={ styles['footer-container'] }>
                    <div className={ styles['footer-brand-info-m'] }>
                        <CompFooterBrandInfo langPack={ props.ctx.i18nPackage } />
                    </div>
                    <div className={ styles['link-groups'] }>
                        <CompFooterLinkGroups />
                    </div>
                    <div className={ styles['footer-brand-info'] }>
                        <CompFooterBrandInfo langPack={ props.ctx.i18nPackage } />
                    </div>
                    <div className={ styles['footer-social'] }>
                        <CompAcceptPayWithSocial />
                    </div>
                    <div className={ styles['footer-copyright'] }>
                        <CompFooterCopyright />
                    </div>
                    <div
                        ref={ otherInfoRef }
                        className={ styles['footer-other-info'] }>
                        <CompFooterOtherInfo />
                    </div>
                </div>
            </FooterContext.Provider>
        </ThemeConfigContext.Provider>
    );
};
export default CompReolinkFooter;
